import React from "react";
import { motion } from "framer-motion";
import {
  FilePlus2,
  PackageSearch,
  Receipt,
  Upload,
  Send,
  Factory,
  CheckSquare,
  Split,
  Target,
} from "lucide-react";

const STEPS = [
  {
    icon: FilePlus2,
    label: "Create RFQ",
    sub: "Inside your Workspace",
    code: "01",
  },
  {
    icon: PackageSearch,
    label: "Define Product",
    sub: "Commodity, quantity, specifications",
    code: "02",
  },
  {
    icon: Receipt,
    label: "Add Commercial Requirements",
    sub: "Incoterm, payment terms, target date",
    code: "03",
  },
  {
    icon: Upload,
    label: "Upload Documents",
    sub: "Tech packs, drawings, brand specs",
    code: "04",
  },
  {
    icon: Send,
    label: "Submit RFQ",
    sub: "One click — Workspace handles routing",
    code: "05",
  },
  {
    icon: Factory,
    label: "Verified Manufacturers Receive It",
    sub: "Pre-qualified Turkish suppliers only",
    code: "06",
  },
];

export default function HowItWorks({ onCta }) {
  return (
    <section
      id="how"
      data-testid="how-section"
      className="relative py-24 sm:py-32 border-t border-black/[0.06]"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <span className="eyebrow">How RFQ™ Works</span>
          <h2 className="mt-4 font-display font-black tracking-[-0.035em] text-[40px] sm:text-[56px] leading-[0.98]">
            From requirement to quotation
            <br />
            <span className="text-black/40">in one continuous flow.</span>
          </h2>
          <p className="mt-5 max-w-2xl text-[15px] leading-relaxed text-black/60">
            A calm procurement workflow designed to move structured requirements into supplier-ready RFQs
            without rework, missing context or email chaos.
          </p>
        </div>

        <div className="mt-14 rounded-[30px] border border-black/[0.06] bg-[#FCFCFD] p-4 shadow-os sm:p-6">
          <div className="rounded-[24px] border border-black/[0.05] bg-white px-4 py-4 shadow-soft sm:px-5">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div>
                <span className="font-mono text-[9px] tracking-[0.22em] text-black/38">
                  WORKFLOW OVERVIEW
                </span>
                <p className="mt-2 max-w-2xl text-[13.5px] leading-relaxed text-black/58">
                  RFQ™ takes one sourcing request from structured input to supplier-ready distribution in a
                  single procurement flow.
                </p>
              </div>
              <div className="grid grid-cols-3 gap-3 lg:min-w-[320px]">
                <div className="rounded-[18px] border border-black/[0.05] bg-[#FAFBFC] px-3 py-3">
                  <div className="font-mono text-[9px] tracking-[0.18em] text-black/38">STEPS</div>
                  <div className="mt-2 font-display text-[24px] font-black leading-none">06</div>
                </div>
                <div className="rounded-[18px] border border-black/[0.05] bg-[#FAFBFC] px-3 py-3">
                  <div className="font-mono text-[9px] tracking-[0.18em] text-black/38">STATUS</div>
                  <div className="mt-2 text-[12px] font-semibold text-black/76">Active</div>
                </div>
                <div className="rounded-[18px] border border-black/[0.05] bg-[#FAFBFC] px-3 py-3">
                  <div className="font-mono text-[9px] tracking-[0.18em] text-black/38">READY</div>
                  <div className="mt-2 text-[12px] font-semibold text-[#0044FF]">72%</div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-[minmax(0,1fr)_280px] lg:gap-5">
            <div className="grid gap-3 sm:grid-cols-2">
              {STEPS.map(({ icon: Icon, label, sub, code }, i) => (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className={`rounded-[22px] border border-black/[0.05] bg-white p-4 shadow-soft transition-all duration-200 hover:-translate-y-0.5 hover:shadow-card sm:p-5 ${
                    i === 0 || i === 3 ? "sm:col-span-2" : ""
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex flex-col items-center">
                      <div className="inline-flex h-11 w-11 items-center justify-center rounded-[14px] border border-black/[0.08] bg-[#FAFBFC] text-[#0A0A0A]">
                        <Icon className="h-5 w-5" strokeWidth={1.75} />
                      </div>
                      <div className="mt-2 font-mono text-[10px] tracking-[0.18em] text-black/36">
                        {code}
                      </div>
                    </div>
                    <div className="min-w-0 flex-1 pt-0.5">
                      <p className="font-mono text-[9px] tracking-[0.22em] text-black/38">STEP {code}</p>
                      <h3 className="mt-1 font-display text-[18px] font-semibold tracking-tight text-[#171717]">
                        {label}
                      </h3>
                      <p className="mt-2 text-[13.5px] leading-relaxed text-black/58">{sub}</p>
                    </div>
                  </div>
                </motion.div>
              ))}

              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.4 }}
                className="rounded-[22px] border border-black/[0.05] bg-white p-5 shadow-soft sm:col-span-2"
              >
                <div className="flex items-start gap-4">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-[14px] border border-[#0044FF]/20 bg-[#0044FF]/8 text-[#0044FF]">
                    <Split className="h-5 w-5" strokeWidth={1.75} />
                  </div>
                  <div className="flex-1">
                    <span className="font-mono text-[10px] tracking-[0.18em] text-black/40">THEN</span>
                    <h3 className="mt-1 font-display text-[20px] font-semibold tracking-tight text-[#171717]">
                      Choose your outcome
                    </h3>
                    <div className="mt-4 grid gap-3 sm:grid-cols-2">
                      <div className="rounded-[18px] border border-black/[0.08] bg-[#FAFBFC] p-4">
                        <div className="flex items-center gap-2">
                          <CheckSquare className="h-4 w-4 text-emerald-600" />
                          <span className="font-display text-[15px] font-semibold tracking-tight">
                            Receive Factory Quotations
                          </span>
                        </div>
                        <p className="mt-1.5 text-[13px] leading-relaxed text-black/60">
                          Direct, comparable quotations from verified Turkish manufacturers.
                        </p>
                      </div>
                      <div className="rounded-[18px] border border-[#0044FF]/20 bg-[#0044FF]/[0.04] p-4">
                        <div className="flex items-center gap-2">
                          <Target className="h-4 w-4 text-[#0044FF]" />
                          <span className="font-display text-[15px] font-semibold tracking-tight">
                            Continue with CommodityBid™
                          </span>
                        </div>
                        <p className="mt-1.5 text-[13px] leading-relaxed text-black/60">
                          Turn the RFQ into a live procurement auction inside the same Workspace.
                        </p>
                      </div>
                    </div>
                    <div className="mt-5">
                      <button
                        data-testid="how-create-rfq-btn"
                        onClick={() => onCta("rfq")}
                        className="btn-primary text-[13.5px] inline-flex items-center gap-2"
                      >
                        Start your first RFQ
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            <aside className="grid gap-3 self-start">
              <div className="rounded-[22px] border border-black/[0.05] bg-white p-4 shadow-soft">
                <span className="font-mono text-[9px] tracking-[0.22em] text-black/38">
                  WORKFLOW STATE
                </span>
                <div className="mt-3 space-y-3">
                  <div>
                    <p className="text-[12px] font-medium text-black/76">Active phase</p>
                    <p className="mt-1 text-[11px] text-black/45">Supplier-ready RFQ preparation</p>
                  </div>
                  <div className="h-1.5 overflow-hidden rounded-full bg-black/[0.06]">
                    <div className="shimmer h-full w-[72%] rounded-full bg-gradient-to-r from-[#0044FF] to-emerald-500" />
                  </div>
                  <p className="text-[11px] text-black/45">6 structured steps complete before distribution.</p>
                </div>
              </div>

              <div className="rounded-[22px] border border-black/[0.05] bg-white p-4 shadow-soft">
                <span className="font-mono text-[9px] tracking-[0.22em] text-black/38">
                  WHY IT FEELS BETTER
                </span>
                <div className="mt-3 space-y-2.5">
                  {[
                    "Requirements stay structured from day one",
                    "Commercial context is defined before outreach",
                    "Documents travel with the RFQ automatically",
                    "The same workflow can continue into CommodityBid™",
                  ].map((item) => (
                    <div key={item} className="flex items-start gap-2 text-[11.5px] text-black/66">
                      <span className="mt-[7px] inline-flex h-1 w-1 shrink-0 rounded-full bg-black/30" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </aside>
          </div>
        </div>
      </div>
    </section>
  );
}
